import type { LucideIcon } from "lucide-react";
import {
  AudioLines,
  Captions,
  MonitorPlay,
  Sparkles,
  Video,
} from "lucide-react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { ZoomMeetingSummaryView } from "../types";
import {
  ASSET_KINDS,
  type AssetDisplayState,
  type AssetKind,
  assetState,
} from "./meetingAssets";
import { meetingLink } from "./meetingLink";

const ICONS: Record<AssetKind, LucideIcon> = {
  screen: MonitorPlay,
  camera: Video,
  audio: AudioLines,
  transcript: Captions,
  summary: Sparkles,
};

// State is carried by colour and motion, and always named in the tooltip, so it
// never depends on colour alone.
const STATE_CLASS: Record<AssetDisplayState, string> = {
  available: "text-foreground",
  processing: "text-amber-600 dark:text-amber-400",
  failed: "text-destructive",
  waiting: "text-muted-foreground motion-safe:animate-pulse",
  none: "text-muted-foreground/30",
};

// Only states with something to go and look at are links. Waiting and none
// would open a meeting page with nothing on it for that asset.
const LINKABLE: ReadonlySet<AssetDisplayState> = new Set([
  "available",
  "processing",
  "failed",
]);

/**
 * One icon per asset (screen, camera, audio, transcript, summary), always all
 * five so the column never reflows between rows.
 *
 * `linkable` is for the desktop list, where each icon opens the meeting at that
 * asset. The mobile app registers no meeting page yet, so it passes false.
 */
export const MeetingAssetIcons = ({
  record,
  linkable = false,
  size = "md",
}: {
  record: ZoomMeetingSummaryView;
  linkable?: boolean;
  size?: "sm" | "md";
}) => {
  const translate = useTranslate();
  const now = Date.now();
  return (
    <TooltipProvider>
      <div className="flex items-center gap-1.5">
        {ASSET_KINDS.map((kind) => {
          const state = assetState(record, kind, now);
          const Icon = ICONS[kind];
          const label = `${translate(`resources.zoom_meetings.assets.${kind}`)}: ${translate(
            `resources.zoom_meetings.asset_state.${state}`,
          )}`;
          const icon = (
            <Icon
              className={cn(
                size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4",
                STATE_CLASS[state],
              )}
            />
          );
          return (
            <Tooltip key={kind}>
              <TooltipTrigger asChild>
                {linkable && LINKABLE.has(state) ? (
                  <Link
                    to={meetingLink(record.id, { asset: kind })}
                    aria-label={label}
                    // The row navigates on click too; without this both fire.
                    onClick={(e) => e.stopPropagation()}
                    className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {icon}
                  </Link>
                ) : (
                  <span aria-label={label}>{icon}</span>
                )}
              </TooltipTrigger>
              <TooltipContent>{label}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
